import { createPortal } from "react-dom"
import { Spin } from "components/ui/spin/Spin"
import { useDocumentLoadStore } from "store/documentLoad.store"
import styles from "./DocumentLoadOverlay.module.scss"

/**
 * The dimmed backdrop and spinner shown while the drawing is downloading.
 *
 * Replaces the indicator Apryse used to provide: it showed one while IT fetched the file through
 * `initialDoc`, but that download was redundant (the orchestrator fetched the same file again), so
 * removing it left the wait silent. Since we own the fetch we report real bytes — see
 * `store/documentLoad.store`.
 *
 * Rendered by `QuotationViewerModal` in the LIGHT dom, deliberately not into the Apryse shadow root:
 * the download starts as soon as the modal opens, which is before the viewer has initialized and
 * therefore before a shadow root exists to paint into.
 *
 * PORTALED TO `document.body`, which is not cosmetic. antd portals the viewer modal there too, so
 * a z-index above the modal's only wins if the two share a stacking context — rendered in place,
 * any ancestor carrying its own lower z-index would bury this behind the modal it is meant to
 * cover. As body children the comparison is direct.
 */
export const DocumentLoadOverlay = () => {
	const isLoading = useDocumentLoadStore((state) => state.isLoading)
	const fraction = useDocumentLoadStore((state) => state.fraction)

	if (!isLoading) return null

	// A response with no Content-Length still downloads; it just cannot be counted, so the spinner
	// carries the wait on its own rather than showing a percentage we would have to invent.
	const percent = fraction === null ? null : Math.round(Math.min(Math.max(fraction, 0), 1) * 100)

	// `output` rather than a div with role="status": it carries that role implicitly, which is what
	// oxlint's prefer-tag-over-role asks for.
	const overlay = (
		<output className={styles.overlay} aria-live="polite" data-testid="document-load-overlay">
			<Spin size="large" />
			<span className={styles.label}>
				{percent === null ? "Loading drawing…" : `Loading drawing… ${percent}%`}
			</span>
		</output>
	)

	return typeof document === "undefined" ? overlay : createPortal(overlay, document.body)
}
